// services/api.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import qs from 'qs';
import { Feed } from '../interfaces/feed';
import { Campaign, FeedMetadata, StatsApiResponse } from './api-interfaces';
import { ReportStats } from './reports-service-reducer';
import { OfferReportApiResponse } from './statsSlice';

export type Id = number | string;
export type NullableId = Id | null;
export interface Paginated<T> {
  total: number;
  limit: number;
  skip: number;
  data: T[];
}

export interface CreateParams {
  service: string;
  entity: any;
}

export interface FindParams {
  service: string;
  query?: any;
}

export interface GetParams {
  service: string;
  _id: string;
}

export interface FinancialReportArgs {
  from: string;
  to: string;
}

export interface DeleteTrafficChannelParams {
  campaignId: string;
  channel: string;
  offerId: string;
}

export interface ChannelStatsReportParams {
  channelTag: string;
  campaignId: string;
}

export interface PublisherChannelConnectionParams {
  campaignId: string;
}

export interface ChannelConnection {
  channelTag: string;
  offerId: string;
  feedTag: string;
  dateConnected: string;
  dateDisconnected: string;
  feedProvider: string;
  offerName?: string;
  stats?: ReportStats;
}

export type PublisherChannelResponse = ChannelConnection[];

export interface BrowserVersionDistribution {
  browserVersion: string;
  visits: number;
}

export interface KeywordDistribution {
  keyword: string;
  visits: number;
}

export interface IPDistribution {
  ip: string;
  visits: number;
}

export interface RefererDistribution {
  refererDomain: string;
  visits: number;
}

export interface TimeOfDayDistribution {
  hourOfDay: number;
  visits: number;
}

export interface TrafficReportPerDay {
  _id: string;
  campaignId: string;
  channelTag: string;
  date: string;
  feedTag: string;
  offerId: string;
  trafficSourceId: string;
  visits: number;
}

export interface CountryCodeDistribution {
  countryCode: string;
  countryName: string;
  visits: number;
}

export interface ChannelStatsReportResponse {
  platform: 'voluum' | 'redtrack';
  keywordDistribution: KeywordDistribution[];
  browserVersionDistribution: BrowserVersionDistribution[];
  IPDistribution: IPDistribution[];
  timeOfDayDistribution: TimeOfDayDistribution[];
  timeOfDayDistributionLast24Hours: TimeOfDayDistribution[];
  countryCodeDistribution: CountryCodeDistribution[];
  refererDomainDistribution: RefererDistribution[];
  feedLogList: Feed[];
  trafficReportPerDay?: any[];
}

// Individual component response types
export interface TimeOfDayDistributionResponse {
  timeOfDayDistribution: TimeOfDayDistribution[];
  timeOfDayDistributionLast24Hours: TimeOfDayDistribution[];
}

export interface OfferDistribution {
  offerName: string;
  offerId: string;
  visits: number;
}

// Wrapper type for API responses that might be rate limited
export interface ApiResponse<T> {
  data: T;
  error: string | null;
  shouldRetry: boolean;
}

const baseUrl = process.env.NEXT_PUBLIC_API_ENDPOINT ?? 'http://localhost:3310';
console.log(`Base url: ${baseUrl}`);

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      // Get the token from the state
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    authenticate: builder.mutation({
      query: (credentials) => ({
        url: '/authentication',
        method: 'POST',
        body: {
          strategy: 'local',
          ...credentials,
        },
      }),
    }),
    // Add the new endpoint for fetching stats
    getStats: builder.query<StatsApiResponse, void>({
      query: () => '/stats',
    }),
    // Get detailed stats mutation
    getDetailedStats: builder.mutation({
      query: (params: { campaign: Campaign }) => ({
        url: '/stats',
        method: 'POST',
        body: params,
      }),
    }),
    // Get detailed daily report query
    // Add the new endpoint for fetching reports by date as input
    getDetailedDailyReport: builder.query({
      query: (date: string) => `/stats/detailed-daily-report?${qs.stringify({ date })}`,
    }),
    getFinancialReport: builder.query({
      query: (args: FinancialReportArgs) => `/stats/financial-report?${qs.stringify(args)}`,
    }),
    getChannelStatsReport: builder.query<ChannelStatsReportResponse, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/channel-stats-report`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    // Individual channel stats component queries
    getChannelKeywordDistribution: builder.query<ApiResponse<KeywordDistribution[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/keyword-distribution`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelIPDistribution: builder.query<ApiResponse<IPDistribution[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/ip-distribution`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelBrowserVersionDistribution: builder.query<ApiResponse<BrowserVersionDistribution[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/browser-version-distribution`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelTimeOfDayDistribution: builder.query<ApiResponse<TimeOfDayDistributionResponse>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/time-of-day-distribution`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelCountryCodeDistribution: builder.query<ApiResponse<CountryCodeDistribution[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/country-code-distribution`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelRefererDomainDistribution: builder.query<ApiResponse<RefererDistribution[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/referer-domain-distribution`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelOfferDistribution: builder.query<ApiResponse<OfferDistribution[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/offer-distribution`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelTrafficReport: builder.query<ApiResponse<any[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/traffic-report`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelFeedLogList: builder.query<ApiResponse<any[]>, ChannelStatsReportParams>({
      query: (params) => ({
        url: `/stats/feed-log-list`,
        params: {
          channelTag: params.channelTag,
          campaignId: params.campaignId,
        },
      }),
    }),
    getCampaignChannelConnections: builder.query<PublisherChannelResponse, PublisherChannelConnectionParams>({
      query: (params) => ({
        url: `/stats/campaign-channel-connections`,
        params: {
          campaignId: params.campaignId,
        },
      }),
    }),
    getChannelsByFeedTag: builder.query<any[], { feedTag: string }>({
      query: (params) => ({
        url: `/channels`,
        params: {
          feedTag: params.feedTag,
        },
      }),
    }),
    // missing stats report
    getMissingStatsReport: builder.query({
      query: () => ({
        url: '/stats/missing-stats-report',
        cachePolicy: 'no-cache',
      })
    }),
    getOfferReport: builder.query<OfferReportApiResponse, { platform?: string } | null>({
      query: (params) => {
        let url = '/stats/offer-report';
        if (params?.platform) {
          url += `?${qs.stringify({ platform: params.platform })}`;
        }
        return {
          url,
          cachePolicy: 'no-cache',
        };
      }
    }),
    // kpi report
    getKPIReport: builder.query({
      query: () => ({
        url: '/stats/kpi-report',
        cachePolicy: 'no-cache',
      })
    }),
    // voluum kpi report
    getVoluumKPIReport: builder.query({
      query: () => ({
        url: '/stats/voluum-kpi-report',
        cachePolicy: 'no-cache',
      })
    }),
    // redtrack kpi report
    getRedTrackKPIReport: builder.query({
      query: () => ({
        url: '/stats/redtrack-kpi-report',
        cachePolicy: 'no-cache',
      })
    }),
    // unconnected traffic report
    getUnconnectedTrafficReport: builder.query({
      query: () => ({
        url: '/stats/unconnected-traffic-report',
        cachePolicy: 'no-cache',
      })
    }),
    getFeedMetadata: builder.query<FeedMetadata, void>({
      query: () => '/feed-metadata/metadata',
    }),
    getReports: builder.query({
      query: () => '/report',
    }),
    getEntity: builder.query<any, GetParams>({
      query: ({ service, _id }) => ({
        url: `${service}/${_id}`,
        method: "GET",
      }),
    }),
    findEntity: builder.query<Paginated<any>, FindParams>({
      query: ({ service, query }) => {
        let url = service;
        if (query) {
          url += `/?${qs.stringify(query)}`;
        }
        return {
          url,
          method: "GET",
        };
      },
    }),
    // Create actions mutation, POST to /actions with cmd and args
    sendAction: builder.mutation({
      query: (params: { cmd: string; params: any }) => ({
        url: "/actions",
        method: "POST",
        body: params,
      }),
    }),
    createEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}`,
        method: "POST",
        body: entity,
      }),
    }),
    patchEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}/${entity._id}`,
        method: "PATCH",
        body: entity,
      }),
    }),
    deleteEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}/${entity._id}`,
        method: "DELETE",
        body: entity,
      }),
    }),
    // Delete traffic channel mutation
    disconnectTrafficChannel: builder.mutation<any, DeleteTrafficChannelParams>({
      query: ({ campaignId, channel, offerId }) => ({
        url: '/actions',
        method: "POST",
        body: {
          cmd: "disconnectTrafficChannel",
          params: {
            campaignId,
            channel,
            offerId
          },
        },
      }),
    }),
    // Define other endpoints here
  }),
});

export const {
  useAuthenticateMutation,
  useGetStatsQuery,
  useGetReportsQuery,
  useGetDetailedDailyReportQuery,
  useSendActionMutation,
  useGetMissingStatsReportQuery,
  useGetUnconnectedTrafficReportQuery,
  useDisconnectTrafficChannelMutation,
  useGetChannelStatsReportQuery,
  useGetChannelKeywordDistributionQuery,
  useGetChannelIPDistributionQuery,
  useGetChannelBrowserVersionDistributionQuery,
  useGetChannelTimeOfDayDistributionQuery,
  useGetChannelCountryCodeDistributionQuery,
  useGetChannelRefererDomainDistributionQuery,
  useGetChannelOfferDistributionQuery,
  useGetChannelTrafficReportQuery,
  useGetChannelFeedLogListQuery,
  useGetCampaignChannelConnectionsQuery,
  useGetChannelsByFeedTagQuery,
  useGetFeedMetadataQuery,
  useGetKPIReportQuery,
  useGetVoluumKPIReportQuery,
  useGetRedTrackKPIReportQuery,
  useGetOfferReportQuery
} = api;

export const {
  getEntity,
  findEntity,
  createEntity,
  patchEntity,
  deleteEntity,
  getDetailedStats,
  getDetailedDailyReport,
  getMissingStatsReport,
  sendAction,
  disconnectTrafficChannel,
  getChannelStatsReport,
  getChannelKeywordDistribution,
  getChannelIPDistribution,
  getChannelBrowserVersionDistribution,
  getChannelTimeOfDayDistribution,
  getChannelCountryCodeDistribution,
  getChannelRefererDomainDistribution,
  getChannelOfferDistribution,
  getChannelTrafficReport,
  getChannelFeedLogList,
  getReports,
  getCampaignChannelConnections
} = api.endpoints;


export type OfferReportQueryHook = typeof api.endpoints.getOfferReport.useQuery;
